import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import { CARDANO_ADA_ASSET_ID } from "@sundae/cardano-utils";
import { useI18N } from "@sundae/react-hooks";
import { Text, Tooltip, toolkitCx } from "@sundae/ui-toolkit";
import { IMappedAsset } from "@sundae/wallet-react";
import { AssetAmount } from "@sundaeswap/asset";
import cx from "classnames";
import { FC, useCallback, useEffect, useMemo } from "react";
import { ADA_METADATA } from "../../../constants/cardano.constants";
import { useYieldFarmingProgramsEstimation } from "../../../gql/hooks/farms.query";
import { useAdaBalance } from "../../../hooks/useAdaBalance";
import { useAssetsWithBalance } from "../../../hooks/useAssetsWithBalance";
import useIsLPAsset from "../../../hooks/useIsLPAsset";
import { IAssetMetaData } from "../../../types/Asset.types";
import { IEnhancedValue } from "../../../types/Farm.types";
import { TAssetIdPoolMap } from "../../../utils/pool.utils";
import { bigintToString } from "../../../utils/string-format";
import AddItemButton from "../../AddItemButton";
import LockedPositionItem from "../LockedPositionItem";
import NewPositionItem from "../NewPositionItem";
import { useYieldFarmingPositionDialogContext } from "../PositionTable/context";

interface IPositionValueListProps {
  isPreview?: boolean;
  isInputBelowAdaMinLimit?: boolean;
  lpAssetsPoolMap: TAssetIdPoolMap;
  isLoading: boolean;
}

const PositionValueList: FC<IPositionValueListProps> = ({
  isInputBelowAdaMinLimit = false,
  lpAssetsPoolMap,
  isLoading,
}) => {
  const { assetsWithBalance } = useAssetsWithBalance();
  const { t } = useI18N("farms");
  const { availableAdaBalance } = useAdaBalance();
  const { queries: estimationQueries, allAreLoaded } = useYieldFarmingProgramsEstimation();
  const { isLPAsset } = useIsLPAsset();
  const {
    onAssetSelect,
    onUpdatePositionsValue,
    onRemovePositionValue,
    positionValues,
    setPositionValues,
    showAssetSelect,
    toggleAssetSelect,
  } = useYieldFarmingPositionDialogContext();
  /**
   * This is a memoized variable containing all assets from a users' wallet that are currently _not_ locked (not available in the `position` array).
   */
  const notLockedAssetsWithBalanceFromValue = useMemo(() => {
    // Get all assetIds from the position
    const positionAssetIds = positionValues?.value.map((item) => item.asset.metadata.assetId) || [];

    // Filter out items from the assets that don't exist in the position
    return assetsWithBalance
      .filter((item) => !positionAssetIds.includes(item.metadata.assetId))
      .map((asset) => {
        const newAssetAmount = new AssetAmount(asset.quantity.amount, asset.metadata);
        return {
          asset,
          assetID: asset.metadata.assetId,
          amount: bigintToString(asset.quantity.amount), // TODO: lovelaces?
          assetAmount: newAssetAmount,
          percentageLocked: 1,
          pool: lpAssetsPoolMap?.get(asset.metadata.assetId),
          totalAssetAmount: asset.quantity,
        };
      })
      .sort((a) => (isLPAsset(a.asset.metadata.policyId) ? -1 : 1)) as IEnhancedValue[];
  }, [lpAssetsPoolMap, positionValues, assetsWithBalance, isLPAsset]);

  const handleAssetSelect = useCallback(
    (asset: IMappedAsset<IAssetMetaData>) =>
      onAssetSelect(asset.metadata.assetId, notLockedAssetsWithBalanceFromValue),
    [notLockedAssetsWithBalanceFromValue]
  );

  useEffect(() => {
    if (!isLoading && !positionValues?.value?.length && availableAdaBalance) {
      setPositionValues((prevValues) => ({
        ...prevValues,
        value: [
          {
            amount: "5000000",
            asset: { metadata: ADA_METADATA, quantity: availableAdaBalance.quantity },
            assetAmount: new AssetAmount(5000000n, ADA_METADATA),
            assetID: ADA_METADATA.assetId,
            percentageLocked: 0,
            totalAssetAmount: availableAdaBalance.quantity,
          },
        ],
      }));
    }
  }, [positionValues, availableAdaBalance, isLoading]);

  return (
    <>
      {/* -------------------------------------------------------------------------------------------------
       * Locked Position Items
       * -----------------------------------------------------------------------------------------------*/}
      <div className="grid gap-2">
        {positionValues?.value?.map((positionValue, index) => {
          let hra: number | undefined;
          allAreLoaded &&
            estimationQueries?.forEach(({ data }) => {
              const { perPoolHRA } = data || {};
              if (hra) {
                return;
              }

              perPoolHRA?.forEach(({ pool, percent }) => {
                if (pool?.poolIdent === positionValue?.pool?.ident && !hra) {
                  hra = percent;
                }
              });
            });

          return (
            <LockedPositionItem
              showRemove={positionValue.assetID !== CARDANO_ADA_ASSET_ID}
              handleRemove={onRemovePositionValue}
              handleUpdate={onUpdatePositionsValue}
              key={`${positionValue.asset.metadata.assetId}-${index}`}
              isInputBelowAdaMinLimit={isInputBelowAdaMinLimit}
              positionValue={positionValue}
              hra={hra}
            />
          );
        })}
      </div>

      {/* -------------------------------------------------------------------------------------------------
       * Add New Asset Button (+)
       * -----------------------------------------------------------------------------------------------*/}
      {Boolean(notLockedAssetsWithBalanceFromValue.length) && !showAssetSelect && (
        <div className="ml-auto w-[calc(100%-22px)]">
          <AddItemButton onClick={toggleAssetSelect} />
        </div>
      )}

      {showAssetSelect && (
        <div className="grid gap-2 motion-safe:animate-slide-up">
          {/* -------------------------------------------------------------------------------------------------
           * The Asset Select List Title and Back Button
           * -----------------------------------------------------------------------------------------------*/}
          <div className="flex items-center gap-2">
            <Tooltip
              trigger={
                <ArrowLeftIcon
                  className={cx("h-3 w-3 cursor-pointer", toolkitCx.layout.text.silent)}
                  onClick={toggleAssetSelect}
                />
              }
              tooltipLabel={t("back", { ns: "common" })}
            />

            <Text size="sm" tag="span" variant="silent">
              {
                // TODO: wording
                t("portfolio.positions.dialog.assetSelect")
              }
            </Text>
          </div>
          {/* -------------------------------------------------------------------------------------------------
           * The Asset Select List (with all assets from the user's wallet that are not currently locked)
           * -----------------------------------------------------------------------------------------------*/}
          <ul className="ml-auto grid w-[calc(100%-22px)] list-none grid-cols-2 gap-2">
            {notLockedAssetsWithBalanceFromValue.map(({ asset, pool, totalAssetAmount }, index) => {
              let hra: number | undefined;
              allAreLoaded &&
                estimationQueries?.forEach(({ data }) => {
                  const { perPoolHRA } = data || {};
                  if (hra) {
                    return;
                  }

                  perPoolHRA?.forEach((data) => {
                    if (data?.pool?.poolIdent === pool?.ident && !hra) {
                      hra = data?.percent;
                    }
                  });
                });

              return (
                <NewPositionItem
                  key={`${index}-${asset.metadata.assetId}`}
                  asset={asset}
                  onClick={handleAssetSelect}
                  pool={pool}
                  totalAssetAmount={totalAssetAmount}
                  hra={hra}
                />
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
};

export default PositionValueList;
